/*
 * Назначение: верхняя панель админки.
 * Функционал: заголовок раздела, индикатор здоровья ИИ, переключатель темы,
 * ссылка на профиль и кнопка выхода.
 */

import { Activity, LogOut, Menu, User } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { useAuth } from "../auth/auth-provider";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { ThemeToggle } from "../theme/theme-toggle";

interface HeaderProps {
  title: string;
  breadcrumbs?: { label: string; path?: string }[];
  systemStatus?: "healthy" | "warning" | "error";
  /** Кнопка меню на телефоне (< md); на ПК меню всегда слева. */
  onMenuClick?: () => void;
  menuOpen?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  owner: "Владелец",
  admin: "Администратор",
  manager: "Менеджер",
  viewer: "Наблюдатель",
};

export function Header({ title, breadcrumbs, systemStatus = "healthy", onMenuClick, menuOpen }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const statusColors = {
    healthy: "bg-success",
    warning: "bg-warning",
    error: "bg-destructive",
  } as const;

  const statusLabels = {
    healthy: "Все системы работают",
    warning: "Есть предупреждения",
    error: "Обнаружены ошибки",
  } as const;

  const handleLogout = () => {
    void logout().finally(() => navigate("/login", { replace: true }));
  };

  const userTooltip = user
    ? `${user.full_name || user.login} · ${ROLE_LABELS[user.role] ?? user.role}`
    : "Профиль";

  return (
    <header className="h-14 md:h-16 shrink-0 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b border-border flex items-center justify-between gap-2 px-2 md:px-6 sticky top-0 z-30">
      {onMenuClick && (
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden shrink-0"
          aria-label="Открыть меню"
          aria-controls="admin-sidebar"
          aria-expanded={!!menuOpen}
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}
      <div className="flex flex-col min-w-0 flex-1">
        <h1 className="text-base md:text-lg font-medium leading-tight truncate">{title}</h1>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {breadcrumbs.map((crumb, index) => (
              <span key={index}>
                {index > 0 && <span className="mx-1">/</span>}
                {crumb.path ? (
                  <button className="hover:text-foreground transition-colors">{crumb.label}</button>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-0.5 md:gap-1.5 shrink-0">
        {/* Здоровье системы (на телефоне не помещается — скрыто) */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/60 border border-border/50">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div className={`h-2 w-2 rounded-full ${statusColors[systemStatus]} shadow-[0_0_0_3px_color-mix(in_oklab,_currentColor_15%,_transparent)]`} />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{statusLabels[systemStatus]}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Переключатель темы */}
        <ThemeToggle />

        {/* Профиль — прямая ссылка на /profile */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="ghost"
                size="icon"
                aria-label="Профиль"
              >
                <Link to="/profile">
                  <User className="h-5 w-5" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{userTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Выйти */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Выйти"
                onClick={handleLogout}
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Выйти</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </header>
  );
}
